import React, { useCallback, useEffect, useState } from 'react';
import OrderableItemList from '../../panel/OrderableItemList';
import BundleSummary from './BundleSummary';
import BundleCustomizationOverlay from './BundleCustomizationOverlay';
import { fetchOmsRequisitionByOrderableItemId } from '../../util/apiUtils';
import { getProfileComponentIds } from '../../panel/panelSelectionUtil';

/**
 * Displays an OrderableItem list and the details of a bundle and facilitates adding, removing, or
 * customizing bundled products.
 */
const EditBundle = ({ bundle, orderableItems, updateBundle }) => {
	const [filter, setFilter] = useState('');
	const [filteredOrderableItemsList, setFilteredOrderableItemsList] = useState([]);
	const [selectedProfiles, setSelectedProfiles] = useState(bundle?.products || []);
	const [customizingProductId, setCustomizingProductId] = useState(null);
	// A cache of fetched requisition structures
	const [omsRequisitions, setOmsRequisitions] = useState(new Map());

	/**
	 * Fetch and cache omsRequisitions every time a new product is added to the bundle.
	 * Initially a 'loading' placeholder object will be added to the omsRequisitions cache. This
	 * object will be replaced with the omsReq data (or an error) once the fetch is finished.
	 */
	const gsdlNo = bundle?.gsdlNo;
	useEffect(() => {
		if (gsdlNo && selectedProfiles?.length) {
			selectedProfiles.forEach((product) => {
				const omsOrderableItemId = product.omsOrderableItemId;
				const cachedOmsReq = omsRequisitions?.get(omsOrderableItemId);
				if (!cachedOmsReq) {
					// Set a placeholder req so we know a fetch is in progress
					updateOmsRequisitionCache(omsOrderableItemId, { loading: true });

					fetchOmsRequisitionByOrderableItemId(omsOrderableItemId, gsdlNo)
						.then((json) => {
							updateOmsRequisitionCache(omsOrderableItemId, json);
						})
						.catch((e) => {
							updateOmsRequisitionCache(omsOrderableItemId, { loading: false, error: true });
						});
				}
			});
		}
	}, [gsdlNo, selectedProfiles, omsRequisitions]);

	/**
	 * Set a new value in the omsRequisitions cache.
	 *
	 * Simply setting a new value in the map won't trigger a useEffect because that doesn't change
	 * the omsRequisitions map identity. Create a new object with `new Map()` to force a new identity.
	 */
	const updateOmsRequisitionCache = (omsOrderableItemId, newValue) => {
		setOmsRequisitions((prevValue) => new Map(prevValue.set(omsOrderableItemId, newValue)));
	};

	/**
	 * omsRequisitions' identity only changes when a new requisition is done being fetched (because we create a new Map()).
	 * When that happens apply new auto-selections logic and update the selected profile with the omsRequisition object.
	 */
	useEffect(() => {
		setSelectedProfiles((profiles) => {
			const updatedProfiles = profiles?.map((profile) => {
				const omsRequisition = !!profile?.omsOrderableItemId && omsRequisitions.get(profile.omsOrderableItemId);
				/**
				 * The profile might already have a placeholder omsRequisition. Check for orderableItem
				 * to know if we need to update the profile with the actual omsRequisition object
				 */
				if (omsRequisition && !profile?.omsRequisition?.orderableItem) {
					// components will be present if this product was loaded from an existing bundle
					let autoSelection;
					if (!profile?.components) {
						autoSelection = getAutoSelection(omsRequisition);
					}

					return {
						...profile,
						omsRequisition: omsRequisition,
						...(!!autoSelection && {
							autoSelect: !!autoSelection.autoSelect,
							components: autoSelection.selectedComponents || [],
							addons: autoSelection.selectedAddOns || [],
							panel: autoSelection.selectedPanel || null,
							patientInstructions: autoSelection.patientInstructions || null
						})
					};
				}
				return profile;
			});
			return updatedProfiles;
		});
	}, [omsRequisitions]);

	const applySearchFilter = (event) => {
		event?.preventDefault && event.preventDefault();
		const searchTerm = event?.target?.value ?? '';
		setFilter(searchTerm);

		let filteredList = [];
		if (searchTerm) {
			const lowerCaseSearchTerm = searchTerm.toLowerCase();
			filteredList = orderableItems?.otherOrderableItems.filter(
				(item) =>
					item.name.toLowerCase().includes(lowerCaseSearchTerm) ||
					item.category.toLowerCase().includes(lowerCaseSearchTerm)
			);
		}

		setFilteredOrderableItemsList(filteredList);
	};

	const clearSearchFilter = () => {
		applySearchFilter('');
	};

	/**
	 * Adds or removes a product from the selected profiles list.
	 *
	 * The omsOrderableItem ID is used to find if the product already exists in the list. If the
	 * product is already in the list it will be removed, otherwise it will be added.
	 *
	 * Auto-selection logic will be applied if we're adding a new product and that product's
	 * omsRequisition was previously fetched.
	 *
	 * @param omsOrderableItemId The ID of the item to add or remove
	 */
	const addOrRemoveProfile = (omsOrderableItemId) => {
		setSelectedProfiles((currentSelections) => {
			const profileIndex = currentSelections?.findIndex((item) => item.omsOrderableItemId === omsOrderableItemId);

			if (profileIndex < 0) {
				const orderableItem = orderableItems?.otherOrderableItems?.find(
					(item) => item.id === omsOrderableItemId
				);

				let autoSelection;
				let omsRequisition = omsRequisitions.get(omsOrderableItemId);
				if (omsRequisition?.orderableItem) {
					autoSelection = getAutoSelection(omsRequisition);
				} else if (omsRequisition?.error) {
					// Try to re-fetch the omsRequisition if a previous fetch resulted in an error
					updateOmsRequisitionCache(omsOrderableItemId, null);
				}

				return [
					...currentSelections,
					{
						omsOrderableItemId: omsOrderableItemId,
						quantity: 1,
						name: orderableItem?.name,
						omsRequisition: omsRequisition,
						...(!!autoSelection && {
							autoSelect: !!autoSelection.autoSelect,
							components: autoSelection.selectedComponents || [],
							addons: autoSelection.selectedAddOns || [],
							panel: autoSelection.selectedPanel || null,
							patientInstructions: autoSelection.patientInstructions || null
						})
					}
				];
			}

			return currentSelections.toSpliced(profileIndex, 1);
		});
	};

	/**
	 * Updates the selected products list with customization details.
	 */
	const onCustomizationSave = useCallback((selections) => {
		if (!selections) {
			return;
		}

		const matcher = (profile) => profile.omsOrderableItemId === selections.orderableItemId;
		const newValues = {
			autoSelect: !!selections.autoSelect,
			components: selections.selectedComponents || [],
			addons: selections.selectedAddOns || [],
			panel: selections.selectedPanel || null,
			patientInstructions: selections.patientInstructions || null
		};
		updateProfile(matcher, newValues);
	}, []);

	/**
	 * A helper method to update one of the selectedProfiles.
	 * A matcher function is passed in that is applied to each profile. Any profile that passes
	 * the matcher will be updated with the new values.
	 *
	 * @param matcher A function that checks if a profile matches the conditions to be updated
	 * @param values An object of values to merge into the matched profile
	 */
	const updateProfile = (matcher, values) => {
		setSelectedProfiles((profiles) => {
			const updatedProfiles = profiles?.map((profile) => {
				if (matcher(profile)) {
					return {
						...profile,
						...values
					};
				}
				return profile;
			});
			return updatedProfiles;
		});
	};

	return (
		<>
			{!!customizingProductId && (
				<BundleCustomizationOverlay
					activeProduct={selectedProfiles?.find(
						(profile) => profile.omsOrderableItemId === customizingProductId
					)}
					onModalHidden={() => setCustomizingProductId(null)}
					onCustomizationSave={onCustomizationSave}
				/>
			)}

			<div className="row">
				<div className="col-lg-11 col-lg-offset-1">
					{!!bundle?.gearBundleId ? (
						<>
							<h2>Edit custom profile</h2>
							<p>Update the included tests or customize Profile and Add-On selections.</p>
						</>
					) : (
						<>
							<h2>Create a custom profile</h2>
							<p>Select one or more tests to create a custom profile.</p>
						</>
					)}
				</div>
			</div>
			<div className="row">
				<div className="col-sm-6 col-md-5 col-lg-4 col-lg-offset-1 col-sm-push-6 col-md-push-7 col-lg-push-6">
					{!!selectedProfiles?.length && (
						<BundleSummary
							bundle={bundle}
							productDetails={selectedProfiles}
							onRemove={addOrRemoveProfile}
							onCustomize={(product) => {
								setCustomizingProductId(product?.omsOrderableItemId);
							}}
							updateBundle={updateBundle}
						/>
					)}
				</div>
				<div className="col-sm-6 col-md-7 col-lg-5 col-lg-offset-1 col-sm-pull-6 col-md-pull-5">
					<div className="form-group has-feedback">
						<input
							className="form-control"
							id="profileSearchBox"
							type="search"
							value={filter}
							onChange={applySearchFilter}
							autoComplete="off"
							placeholder="Search for a product"
						/>
						{!!filter && (
							<i
								className="glyphicon glyphicon-remove form-control-feedback text-muted"
								style={{ pointerEvents: 'all', cursor: 'pointer' }}
								onClick={clearSearchFilter}
								title="Clear Search"
							></i>
						)}
					</div>
					<OrderableItemList
						orderableItems={orderableItems}
						filteredOrderableItemsList={filteredOrderableItemsList}
						expandedOrderableItemList="recent"
						profileFilter={filter}
						onAccordionToggle={() => {
							/*No-op*/
						}}
						gsdlNo={bundle?.gsdlNo}
						displayQuantity={false}
						displayInfo={false}
						onPanelSelection={addOrRemoveProfile}
						selectedProduct={null}
						dropShip={true}
						quantityMap={selectedProfiles}
						displayAddRemove={true}
					/>
				</div>
			</div>
		</>
	);
};

/**
 * Build an object representing automatic selections of the profile and its components.
 * No selections will be built if more than one profile is available on the requisition.
 */
const getAutoSelection = (omsRequisition) => {
	if (omsRequisition?.orderableItem?.profileList?.length === 1 && !omsRequisition.orderableItem.profileList[0]?.displayOnly) {
		const profile = omsRequisition.orderableItem.profileList[0];
		const panelId = profile.id;
		const components = getProfileComponentIds(profile.profileComponents);

		return {
			autoSelect: true,
			orderableItemId: omsRequisition.orderableItem.id,
			selectedPanel: panelId,
			selectedComponents: components,
			selectedAddOns: []
		};
	}
};

export default EditBundle;
